import { useMutation, useQuery } from '@tanstack/react-query';
import {
  leadOpsApi,
  LeadOpsCheckSwResponse,
  LeadOpsCheckMondayResponse,
  LeadOpsActivityLogResponse,
  AdvertiserOption,
  InventoryPitchPayload,
  InventoryPitchResponse,
  UnsoldInventoryResponse,
} from '../api/leadOps';

type LeadOpsApiResponse<T> = {
  success: boolean;
  data: T;
  error?: string;
};

export const useLeadOpsConfig = () => {
  return useQuery<LeadOpsApiResponse<any>>({
    queryKey: ['lead-ops', 'config'],
    queryFn: () => leadOpsApi.getConfig(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeadOpsHistory = () => {
  return useQuery<LeadOpsApiResponse<{ items: Array<{ domain: string; searched_at?: string }> }>>({
    queryKey: ['lead-ops', 'history'],
    queryFn: () => leadOpsApi.getHistory(20),
    staleTime: 30 * 1000,
  });
};

export const useLeadOpsCheckSw = () => {
  return useMutation<LeadOpsApiResponse<LeadOpsCheckSwResponse>, Error, { domain: string; mode?: 'full' | 'light' }>({
    mutationFn: (payload) => leadOpsApi.checkSw(payload.domain, payload.mode || 'full'),
  });
};

export const useLeadOpsCheckMonday = () => {
  return useMutation<LeadOpsApiResponse<LeadOpsCheckMondayResponse>, Error, string>({
    mutationFn: (domain: string) => leadOpsApi.checkMonday(domain),
  });
};

export const useLeadOpsTrafficSources = () => {
  return useMutation<LeadOpsApiResponse<any>, Error, string>({
    mutationFn: (domain: string) => leadOpsApi.getTrafficSources(domain),
  });
};

export const useLeadOpsCompetitors = () => {
  return useMutation<LeadOpsApiResponse<any>, Error, string>({
    mutationFn: (domain: string) => leadOpsApi.getCompetitors(domain),
  });
};

export const useLeadOpsActivityLog = () => {
  return useMutation<
    LeadOpsApiResponse<LeadOpsActivityLogResponse>,
    Error,
    { item_id: string; board_id: string; limit?: number }
  >({
    mutationFn: (payload) => leadOpsApi.getActivityLog(payload.item_id, payload.board_id, payload.limit || 50),
  });
};

export const useLeadOpsCreate = () => {
  return useMutation<
    LeadOpsApiResponse<{ item_id: string; url?: string }>,
    Error,
    { domain_canonical: string; sw_snapshot?: Record<string, any> }
  >({
    mutationFn: (payload: { domain_canonical: string; sw_snapshot?: Record<string, any> }) =>
      leadOpsApi.create(payload.domain_canonical, payload.sw_snapshot),
  });
};

export const useLeadOpsReassign = () => {
  return useMutation<
    LeadOpsApiResponse<{ ok: boolean; url?: string }>,
    Error,
    { item_id: string; board_id?: string; to_owner_id?: number }
  >({
    mutationFn: (payload: { item_id: string; board_id?: string; to_owner_id?: number }) =>
      leadOpsApi.reassign(payload.item_id, payload.board_id, payload.to_owner_id),
  });
};

export const useLeadOpsMarkDuplicate = () => {
  return useMutation<
    LeadOpsApiResponse<{ ok: boolean; name?: string }>,
    Error,
    { item_id: string; board_id: string; current_name: string }
  >({
    mutationFn: (payload: { item_id: string; board_id: string; current_name: string }) =>
      leadOpsApi.markDuplicate(payload.item_id, payload.board_id, payload.current_name),
  });
};

export const useAdvertiserSearch = (query: string) => {
  const trimmed = query.trim();
  return useQuery<LeadOpsApiResponse<{ items: AdvertiserOption[] }>>({
    queryKey: ['lead-ops', 'advertisers', trimmed],
    queryFn: () => leadOpsApi.searchAdvertisers(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: 60 * 1000,
  });
};

export const useCreateInventoryPitch = () => {
  return useMutation<LeadOpsApiResponse<InventoryPitchResponse>, Error, InventoryPitchPayload>({
    mutationFn: (payload: InventoryPitchPayload) => leadOpsApi.createInventoryPitch(payload),
  });
};

export const useUnsoldInventory = (enabled: boolean) => {
  return useQuery<LeadOpsApiResponse<UnsoldInventoryResponse>>({
    queryKey: ['lead-ops', 'unsold-inventory'],
    queryFn: () => leadOpsApi.getUnsoldInventory(),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
